<template>
  <EditorPanel class="pb-data-panel">
    <template #header>
      <ObjectHeader :icon="panelIcon" />
    </template>
    <template v-if="channel">
      <ReportMessage
        :item="channel"
        :default-state="true"
        :children-state="false"
      >
        {{ translate('pb2.channelPropertiesView.channelReportTitle') }}
      </ReportMessage>
      <ItemProperties class="pb-channel-properties">
        <h3>{{ translate('pb2.channelPropertiesView.channelPropertiesTitle') }}</h3>
        <InputWithLoader
          :label="translate('pb2.channelPropertiesView.channelNameLabel')"
          :placeholder="translate('pb2.channelPropertiesView.channelNamePlaceholder')"
          :value="channel.data.name"
          :disabled="readonly"
          class="pb-half-margin-top"
          @input="set('name', $event)"
        />
        <UiField
          nolabel
          nofocus
          :label="translate('pb2.channelPropertiesView.channelTypeLabel')"
          class="pb-quarter-margin-top"
        >
          <span class="txt-muted txt-right">{{ channelType }}</span>
        </UiField>
        <template v-if="!channel.data.lsb">
          <InputWithLoader
            v-if="!isGlobal"
            v-tooltip="{
              text: translate('pb2.channelPropertiesView.channel16bitsTooltip'),
            }"
            :type="INPUT_TYPE_SWITCH"
            :label="translate('pb2.channelPropertiesView.channel16bitsLabel')"
            :value="channel.data.msb"
            :disabled="readonly"
            class="pb-quarter-margin-top"
            @input="set('msb', $event)"
          />
          <ColorInputWithLoader
            v-if="typeof channel.data.color_min !== 'undefined'"
            :label="translate('pb2.channelPropertiesView.channelColorMinLabel')"
            :value="channel.data.color_min"
            :disabled="readonly"
            class="pb-quarter-margin-top"
            @input="setColor('min', $event)"
          />
          <ColorInputWithLoader
            v-if="typeof channel.data.color_max !== 'undefined'"
            :label="translate('pb2.channelPropertiesView.channelColorMaxLabel')"
            :value="channel.data.color_max"
            :disabled="readonly"
            class="pb-quarter-margin-top"
            @input="setColor('max', $event)"
          />
          <template v-if="profileBeams && profileBeams.length > 1">
            <h3 class="pb-half-margin-top">
              {{ translate('pb2.channelPropertiesView.channelBeamsSelectionTitle') }}
            </h3>
            <BeamsSelector
              v-model:selection="beams"
              :disabled="readonly || beamsLoading"
            />
            <ButtonWithLoader
              icon="icon-save"
              :disabled="!beamsTouched"
              class="pb-quarter-margin-top"
              @click="setBeams"
            />
          </template>
        </template>
      </ItemProperties>
      <PanelMessage v-if="channel.data.lsb">
        {{ translate('pb2.channelPropertiesView.lsbChannelMessage') }}<br>
        {{ translate('pb2.channelPropertiesView.editMsbChannelMessage') }} <PbLink :to="channel.msb.link">
          {{ translate('pb2.channelPropertiesView.editMsbChannelLink') }}
        </PbLink>
      </PanelMessage>
      <ConfirmDialog
        v-bind="controlChannelConfirm"
        confirm-style="error"
        confirm-text="pb2.channelPropertiesView.channelDeleteConfirmLabel"
        :dialog-title="translate('pb2.channelPropertiesView.channelDeleteConfirmTitle')"
      >
        {{ translate('pb2.channelPropertiesView.channelDeleteConfirmMessage') }} "{{ channel.linkName }}" ?
      </ConfirmDialog>
      <ConfirmDialog
        v-if="presetRange"
        v-bind="controlPresetRangeConfirm"
        confirm-style="error"
        confirm-text="pb2.channelPropertiesView.presetRangeDeleteConfirmLabel"
        :dialog-title="translate('pb2.channelPropertiesView.presetRangeDeleteConfirmTitle')"
      >
        {{ translate('pb2.channelPropertiesView.presetRangeDeleteConfirmMessage') }} "{{ presetRange.linkName }}" ?
      </ConfirmDialog>
      <ButtonGroup
        column
        class="pb-channel-actions"
      >
        <ButtonWithLoader
          v-if="(!isLsbChannel && presetRange)"
          :disabled="readonly"
          @click="duplicatePresetRange"
        >
          <span class="ellipsis">{{ translate('pb2.channelPropertiesView.duplicatePresetRangeLabel') }}</span>
        </ButtonWithLoader>
        <ButtonWithLoader
          v-if="!isLsbChannel"
          :disabled="readonly"
          @click="duplicateChannel"
        >
          <span class="ellipsis">{{ translate('pb2.channelPropertiesView.duplicateChannelLabel') }}</span>
        </ButtonWithLoader>
        <ButtonWithLoader
          v-if="channel.data._linkedChannel && channel.data.hasLocalChanges"
          :disabled="readonly"
          @click="resetToGlobal"
        >
          <span class="ellipsis">{{ translate('pb2.channelPropertiesView.resetChannelToTemplateLabel') }}</span>
        </ButtonWithLoader>
        <ButtonWithLoader
          v-if="presetRangeCanBeDeleted"
          class="btn-error"
          :disabled="readonly"
          @click="openPresetRangeConfirm"
        >
          <span class="ellipsis">{{ translate('pb2.channelPropertiesView.presetRangeDeleteLabel') }}</span>
        </ButtonWithLoader>
        <ButtonWithLoader
          v-if="channelCanBeDeleted"
          class="btn-error"
          :disabled="readonly"
          @click="openChannelConfirm"
        >
          <span class="ellipsis">{{ translate('pb2.channelPropertiesView.channelDeleteLabel') }}</span>
        </ButtonWithLoader>
      </ButtonGroup>
    </template>
  </EditorPanel>
</template>

<script setup>
import { computed, ref } from 'vue'

import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import UiField from '@nicolaudie-group/ng-ui-kit/components/UiField/UiField.vue'
import { INPUT_TYPE_SWITCH } from '@nicolaudie-group/ng-ui-kit/components/BaseInput/symbols'
import useRefSync from '@nicolaudie-group/ng-ui-kit/composables/useRefSync'
import useCloseOthers from '@nicolaudie-group/ng-ui-kit/composables/useCloseOthers'
import useConfirmDialog from '@nicolaudie-group/ng-ui-kit/composables/useConfirmDialog'

// Import stores
import useChannelStore from '@/stores/channel.store'
import usePresetRangeStore from '@/stores/presetRange.store'
import hasChanged from '@/helpers/hasChanged'

// Import composables
import useIsGlobal from '@/composables/useIsGlobal'
import useDeleteHotkey from '@/composables/useDeleteHotkey'
import {
  useCurrent,
  useConstants,
  useBeams,
  useReadonly,
} from '@/composables/useProfile'
import useTranslate from '@/composables/useTranslate'

// Import components
import BeamsSelector from '@/components/Beams/BeamsSelector.vue'
import InputWithLoader from '@/components/InputWithLoader.vue'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import EditorPanel from '@/components/EditorPanel/EditorPanel.vue'
import ReportMessage from '@/components/ReportMessage/ReportMessage.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue'
import ItemProperties from '@/components/ItemProperties/ItemProperties.vue'
import PbLink from '@/components/PbLink.vue'
import ObjectHeader from '@/components/ObjectHeader/ObjectHeader.vue'
import PanelMessage from '@/components/PanelMessage/PanelMessage.vue'
import ColorInputWithLoader from '@/components/ColorInputWithLoader.vue'

import { channelHelpers } from '@/helpers/profile'
import routeNames from '@/router/names'

// import fillBeams, { SHAPES } from '@/components/Beams/fillBeams'

// Initialize composables & stores
const { isGlobal } = useIsGlobal()

const { translate } = useTranslate()

const channelStore = useChannelStore()
const presetRangeStore = usePresetRangeStore()

// Get the channel
const {
  channel,
  presetRanges,
  presetRange,
} = useCurrent()
const constants = useConstants()
const readonly = useReadonly()

const isLsbChannel = computed(() => !!channel.value && channel.value.data.lsb)

const channelCanBeDeleted = computed(() => !!channel.value)
const presetRangeCanBeDeleted = computed(() => !!presetRange.value && presetRanges.value.length > 1)

// Compute channel_type name
// TODO: check if computed is actually necessary here
const channelType = computed(() => {
  const channelTypeId = channel.value.data.channel_type
  if (constants.value.channels) {
    const { names } = constants.value.channels
    return names[channelTypeId] || `CH ${channelTypeId}`
  }
  return `CH ${channelTypeId}`
})

// Handle edition
const edit = (payload) => {
  if (isGlobal.value) {
    return channelStore.editGlobal(payload)
  }
  return channelStore.editLocal(payload)
}
const resetToGlobal = () => channelStore.resetToGlobal()
const set = (key, value) => edit({ [key]: value })

const setColor = (key, value) => set(`color_${key}`, value.dec)

const profileBeams = useBeams()
const channelBeams = computed(() => (channel.value ? channel.value.data.beams : []))

const beams = useRefSync(channelBeams)
const lastSavedBeams = useRefSync(channelBeams)
const beamsTouched = computed(() => hasChanged(beams.value, lastSavedBeams.value))
const beamsLoading = ref(false)
const setBeams = async () => {
  const channelUidBeingSaved = channel.value.data._uid
  const beamsValue = beams.value
  beamsLoading.value = true
  await edit({ beams: beamsValue })
  beamsLoading.value = false
  // This panel is reused when navigating between channels, so don't mark
  // the beams as saved if another channel got selected during the save
  const channelUidBeingEdited = channel.value?.data._uid
  if (channelUidBeingEdited === channelUidBeingSaved) {
    lastSavedBeams.value = beamsValue
  }
}

const panelIcon = computed(() => channelHelpers.getIcon(channel.value?.data))

function duplicateChannel() {
  if (isGlobal.value) {
    return channelStore.duplicateGlobal()
  }
  return channelStore.duplicateLocal()
}

function duplicatePresetRange() {
  if (isGlobal.value) {
    return presetRangeStore.duplicateGlobal()
  }
  return presetRangeStore.duplicateLocal()
}

function deleteChannel() {
  if (isGlobal.value) {
    return channelStore.deleteGlobal()
  }
  return channelStore.deleteLocal()
}

function deletePresetRange() {
  if (isGlobal.value) {
    return presetRangeStore.deleteGlobal()
  }
  return presetRangeStore.deleteLocal()
}

const {
  control: controlChannelConfirm,
  open: openChannelConfirm,
  cancel: cancelChannelConfirm,
  state: deleteChannelConfirmState,
} = useConfirmDialog(deleteChannel)
useCloseOthers(cancelChannelConfirm, deleteChannelConfirmState)

const {
  control: controlPresetRangeConfirm,
  open: openPresetRangeConfirm,
  cancel: cancelPresetRangeConfirm,
  state: deletePresetRangeConfirmState,
} = useConfirmDialog(deletePresetRange)
useCloseOthers(cancelPresetRangeConfirm, deletePresetRangeConfirmState)

const deleteHotkeyEnabled = computed(() => {
  const channelConfirmOpen = deleteChannelConfirmState.value
  const presetRangeConfirmOpen = deletePresetRangeConfirmState.value
  return !channelConfirmOpen && !presetRangeConfirmOpen
})

const deleteHotkeyOnRoutes = [
  routeNames.LocalPresetRange,
  routeNames.GlobalPresetRange,
  routeNames.LocalChannel,
  routeNames.GlobalChannel,
]

useDeleteHotkey(deleteHotkeyOnRoutes, deleteHotkeyEnabled, () => {
  if (presetRangeCanBeDeleted.value) {
    return openPresetRangeConfirm()
  }
  if (channelCanBeDeleted.value) {
    return openChannelConfirm()
  }
  return undefined
})
</script>

<style lang="scss">
@use 'sass:math';
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '/src/styles/_pb.scss';

.pb-channel-properties {
  .pb-beams-grid {
    margin-left: - pb.$spacing;
    margin-right: - pb.$spacing;
    padding-left: pb.$spacing;
  }

  .pb-beams-row {
    &:after {
      content: '';
      display: inline-block;
      width: pb.$spacing;
      height: 1px;
    }
  }
}

.pb-channel-actions {
  @include flexbox.flex-grow(1);
  @include flexbox.justify-content(flex-end);
  padding-top: pb.$spacing;
}
</style>
